// The shell's own classes — .screen, .shell, .sidenav, .main, .topbar, .content
// and the rest — are vendored in expert-queries-dashboard.css, because that is
// the screen the design draws the chrome on. Imported HERE rather than in that
// route: the shell wraps every authenticated screen, so styling it at the leaf
// would leave the Vetting tab and the rest with an unstyled sidebar.

import { isSessionInvalid } from "@anthra/platform/errors"
import { ErrorState, Loading } from "@anthra/ui/feedback"
import {
	createFileRoute,
	Navigate,
	Outlet,
	useNavigate,
	useRouterState,
} from "@tanstack/react-router"

import { PanelShell } from "@/components/panel-shell"
import { usePanelSession, usePanelSignOut } from "@/features/auth/use-panel-auth"
import { canManageExperts } from "@/features/experts/use-experts"
import { useSessionStore } from "@/stores/use-session-store"

/**
 * The panel shell — a pathless layout, so one check guards every screen.
 *
 * `isActive` IS RE-READ ON EVERY REQUEST, not only at OTP issue: a Super Admin's
 * deactivation lands on the Expert's very next call, and this layout returns them
 * to the login screen (US-01-005.17, US-00-006.15).
 *
 * THE STORE IS CHECKED BEFORE THE SESSION QUERY, and that order is the whole
 * point. Guarding on the session query alone only catches a session that was
 * already gone at page load, because that is the only case in which the query
 * itself 401s. When the Expert is deactivated MID-SESSION the 401 arrives on
 * whatever they just did — a filter on the dashboard — while `usePanelSession()`
 * is sitting on a successful result with five minutes of `staleTime` left. The
 * 401 handler empties the cache, but `queryClient.clear()` DESTROYS query objects
 * rather than refetching them: a mounted observer keeps its last result and never
 * asks again. So the session query stayed `success`, this guard kept passing, and
 * the Expert sat on the dashboard reading an inline "Session expired" error that
 * no navigation ever followed. `status` is the only thing that observes the 401
 * itself, which is why ../stores/use-session-store calls it the mirror the router
 * reads — this is the router reading it.
 *
 * Expert management is rendered only for a super_admin (US-01-005.5). The hiding
 * is cosmetic — the API answers 403 for a role of `expert` and writes the attempt
 * to the audit log (US-01-005.20) — and this navigation entry is not a
 * permission.
 */
export const Route = createFileRoute("/_panel")({
	component: PanelLayout,
})

/** The chrome's page label, by route — see PanelShell's own note on why this is
 *  a label rather than a second `<h1>` competing with the screen's own. */
function chromeTitle(pathname: string): string {
	if (pathname === "/") return "All queries"
	if (pathname.startsWith("/vetting")) return "Vetting"
	if (pathname.startsWith("/reporting")) return "Reporting"
	if (pathname.startsWith("/experts")) return "Experts"
	return "Swamithra Expert panel"
}

function PanelLayout() {
	const navigate = useNavigate()
	const session = usePanelSession()
	const signOut = usePanelSignOut()
	const pathname = useRouterState({ select: state => state.location.pathname })
	const sessionStatus = useSessionStore(state => state.status)

	// First, and ahead of the query: see the note above on a mid-session 401.
	if (sessionStatus === "signed_out") {
		return (
			<Navigate
				to="/login"
				replace
			/>
		)
	}

	if (session.isPending) return <Loading label="Checking your panel session…" />

	// A DROPPED CONNECTION IS NOT A SIGN-OUT — the same fault the user app had.
	// Any failure of `GET /expert/me`, including the service being unreachable,
	// threw the Expert out to the login form mid-shift. `isSessionInvalid` is
	// the one that means signed out; everything else gets a retry.
	if (session.isError && !isSessionInvalid(session.error)) {
		return (
			<div className="mx-auto w-full max-w-md px-4 py-16">
				<ErrorState
					error={session.error}
					onRetry={() => {
						void session.refetch()
					}}
				/>
			</div>
		)
	}

	if (session.isError || !session.data) {
		return (
			<Navigate
				to="/login"
				replace
			/>
		)
	}

	const title = chromeTitle(pathname)

	return (
		<PanelShell
			title={title}
			fullName={session.data.fullName}
			roleLabel={session.data.role === "super_admin" ? "Super Admin" : "Expert"}
			showExperts={canManageExperts(session.data)}
			signOutPending={signOut.isPending}
			onSignOut={() => {
				signOut.mutate(undefined, {
					onSettled: () => {
						void navigate({ to: "/login", replace: true })
					},
				})
			}}
		>
			<Outlet />
		</PanelShell>
	)
}
